import { Table, TableProps } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

// components
import ListButton from 'components/listButton/listButton';
import ActionListHeaderComponent from 'modules/main/actions/listHeader';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import NoData from 'components/noData/noData';
import StatusComponent from 'components/status/status';

// services

// styles

// models
import { UpdateDocumentListItemRequest } from '../models/updateDocument';
import { onGetUpdateDocumentsRequestList } from '../services/updateDocumentsServices';
import NotificationService from 'core/services/notification';
import TableTotal from 'components/tableTotal/tableTotal';
import { isRTL } from 'core/services/language';
import { StatusColors } from 'utils/statusColors';
import { ServiceCost } from '../../commonModels';
import { onGetServiceCost } from '../../commonServices';

interface UpdateDocumentsListPageProps {}

const UpdateDocumentsListPage: FunctionComponent<UpdateDocumentsListPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [requests, setRequests] = useState<UpdateDocumentListItemRequest[]>([]);

	const [serviceCostLoading, setServiceCostLoading] = useState<boolean>(true);
	const [serviceCost, setServiceCost] = useState<ServiceCost>();

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const columns: TableProps['columns'] = [
		{
			title: t('ACTIONS.REQUEST_CODE'),
			dataIndex: 'code',
			key: 'code',
			render: (code) => <>#{code}</>,
		},
		{
			title: t('ACTIONS.REQUEST_DATE'),
			dataIndex: 'created_at',
			key: 'created_at',
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status) => <StatusComponent text={t(`COMMON.${status.toUpperCase()}`)} color={StatusColors[status]} />,
		},
		{
			title: t('ACTIONS.DOCUMENT_TYPE'),
			dataIndex: 'translated_document_name',
			key: 'translated_document_name',
			render: (documentName) => (isRTL() ? documentName.ar : documentName.en),
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: 'actions',
			key: 'actions',
			render: (_, record) => <ListButton type="view" onClick={() => navigate(`${record.id}`)} />,
		},
	];

	useEffect(() => {
		setCurrentPage(getCurrentPage());
		getUpdateDocumentList();
		getServiceCost();
	}, []);

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	const getServiceCost = async () => {
		try {
			setServiceCostLoading(true);

			const response = await onGetServiceCost('update_company_documents');
			setServiceCost(response?.success);

			setServiceCostLoading(false);
		} catch (error) {
			setServiceCostLoading(false);
		}
	};

	const getUpdateDocumentList = async () => {
		try {
			setIsLoading(true);

			const response = await onGetUpdateDocumentsRequestList(getCurrentPage());

			setRequests(response.update_document_actions);
			setTotalRecords(response.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });

			setIsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetUpdateDocumentsRequestList(getCurrentPage());
			setRequests(response.update_document_actions);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	return (
		<>
			<ActionListHeaderComponent
				title={t('ACTIONS.UPDATE_DOCUMENTS')}
				subTitle={t('ACTIONS.TO_UPDATE_COMPANY_DOCUMENTS')}
				costLoading={serviceCostLoading}
				costs={[serviceCost!]}
			/>

			<Table
				columns={columns}
				dataSource={requests}
				scroll={{ x: 600 }}
				rowKey="id"
				pagination={{
					pageSize: 15,
					total: totalRecords,
					showSizeChanger: false,
					current: currentPage,
					onChange: fetchMoreData,
					showTotal: (total) => <TableTotal total={total} />,
				}}
				loading={isLoading}
				locale={{ emptyText: <NoData /> }}
			/>
		</>
	);
};

export default UpdateDocumentsListPage;
